import React, { useMemo, useState } from 'react';
import type { MenuItem, DishSnapshot } from '../../../shared/types';

interface DishPickerProps {
    menu: MenuItem[];
    currentItems: DishSnapshot[];
    onAdd: (item: MenuItem) => void;
    onRemove: (itemName: string) => void;
}

const DishPicker: React.FC<DishPickerProps> = ({ menu, currentItems, onAdd, onRemove }) => {
    const categories = useMemo(() => {
        const set = Array.from(new Set(menu.map(i => i.category).filter(Boolean)));
        return set.length > 0 ? set : ['Plats'];
    }, [menu]);

    const [selectedCategory, setSelectedCategory] = useState<string>(categories[0] || 'Plats');

    const filteredItems = useMemo(
        () => menu.filter(i => i.category === selectedCategory && i.available !== false),
        [menu, selectedCategory],
    );

    const quantityFor = (name: string) =>
        currentItems.find(d => d.name === name)?.quantity ?? 0;

    return (
        <div className="space-y-3">
            {/* Catégories */}
            <div className="flex gap-2 overflow-x-auto pb-1">
                {categories.map(cat => (
                    <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                            selectedCategory === cat
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Grille d'items */}
            {filteredItems.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-6">
                    Aucun plat dans cette catégorie
                </p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {filteredItems.map(item => {
                        const qty = quantityFor(item.name);
                        return (
                            <div
                                key={item.id}
                                className={`p-3 rounded-lg border transition-colors ${
                                    qty > 0
                                        ? 'border-indigo-400 bg-indigo-50'
                                        : 'border-gray-200 bg-white hover:bg-gray-50'
                                }`}
                            >
                                <div className="text-sm font-semibold text-gray-900 truncate">{item.name}</div>
                                <div className="text-xs text-gray-500 mt-0.5">{item.price.toFixed(2)}€</div>
                                <div className="flex items-center gap-2 mt-2">
                                    <button
                                        type="button"
                                        onClick={() => onRemove(item.name)}
                                        disabled={qty === 0}
                                        aria-label={`Retirer ${item.name}`}
                                        className="w-6 h-6 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 text-xs font-bold"
                                    >
                                        −
                                    </button>
                                    <span className="text-xs font-bold text-gray-700 w-6 text-center">{qty}</span>
                                    <button
                                        type="button"
                                        onClick={() => onAdd(item)}
                                        aria-label={`Ajouter ${item.name}`}
                                        className="w-6 h-6 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-bold"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DishPicker;
